import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as i18next from 'i18next';
import moment from 'moment';
import { G2S } from 'core/g2s';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

interface FlightData {
    class: string;
    departure: string;
    from: string;
    passengers: string;
    return: string | null;
    to: string;
}

@WithRender
@Component
export default class FlightsSearchResultsSidebarComponent extends Vue {
    @Prop()
    filters: flights.FlightFilterService;

    @Prop()
    departure: api.Destination;

    @Prop()
    arrival: api.Destination;

    @Prop({ type: String })
    searchId: string | null;

    filterTooltipActivator: any = null;
    filterTooltipVisible: boolean = false;
    filterTooltipTimeout: number | null = null;

    flightDurationRanges: number[][] = [];
    departureArrivalRanges: {
        departureRange: number[];
        arrivalRange: number[];
    }[] = [];
    priceRange: number[] = [0, 0];
    stopDurationRange: number[] = [0, 0];

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    gaAlreadySubmitted: string[] = [];

    flightSearch = new flights.FlightSearchService();
    flightData: FlightData | null = null;

    subscriptionModal = {
        email: '',
        onlyFreeBaggage: false,
        onlyDirectFlight: false,
        view: 'form'
    }

    showSubscriptionButton = false;

    mounted() {
        this.priceRange = [this.filters.price.rangeMin, this.filters.price.rangeMax];
        this.stopDurationRange = [this.filters.stopDuration.rangeMin, this.filters.stopDuration.rangeMax];

        this.filters.flightDuration.items.forEach((item) => {
            this.flightDurationRanges.push([item.rangeMin, item.rangeMax]);
        });
        this.filters.departureArrival.items.forEach((item) => {
            this.departureArrivalRanges.push({
                departureRange: [item.departure.rangeMin, item.departure.rangeMax],
                arrivalRange: [item.arrival.rangeMin, item.arrival.rangeMax]
            });
        });

        const mo = utils.Cookies.getMarkerOwner();

        if (G2S.config.theme !== 'aviasales' && ['219038', ''].includes(mo)) {
            this.showSubscriptionButton = true;
        }
    }

    durationFormat(mins: number): string {
        return (<any>moment.duration(mins, 'minutes')).format(i18next.t('format_duration'));
    }

    flightDurationLabel(itemIndex: number, rangeIndex: number): string {
        if (this.flightDurationRanges
            && this.flightDurationRanges.length > 0
            && this.flightDurationRanges[itemIndex]) {
            return this.durationFormat(this.flightDurationRanges[itemIndex][rangeIndex]);
        } else {
            return '';
        }
    }

    departureArrivalTimeLabel(unixTime) {
        return moment.unix(unixTime).utc().format(i18next.t('format_time'));
    }

    departureArrivalDayLabel(unixTime) {
        return moment.unix(unixTime).utc().format(i18next.t('format_date_md_short'));
    }

    onFilterCheckboxChange(e: any) {
        this.showFilterTooltip(e.target.parentNode);

        if(e.target.checked) {
            const idCheckbox = e.target.id.includes('filter') ? e.target.id.replace(/[0-9]/g, '') : e.target.id;
            
            switch (idCheckbox) {
                case 'checkboxStops0':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.direct.only', 'flights.results.filters.apply.direct.only', this.gaParams);
                    break;
                case 'checkboxStops1':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.one.transfer', 'flights.results.filters.apply.one.transfer', this.gaParams);
                    break;
                case 'checkboxStops2':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.many.transfer', 'flights.results.filters.apply.many.transfer', this.gaParams);
                    break;
                case 'checkboxBaggage0':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.not.baggage', 'flights.results.filters.apply.not.baggage', this.gaParams);
                    break;
                case 'checkboxBaggage1':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.ok.baggage', 'flights.results.filters.apply.ok.baggage', this.gaParams);
                    break;
                case 'filterAirportsDeparture':
                case 'filterAirportsArrival':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.airport', 'flights.results.filters.apply.airport', this.gaParams);
                    break;
                case 'filterAirline':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.airline', 'flights.results.filters.apply.airline', this.gaParams);
                    break;
                case 'filterAirlineAlliance':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.alliance', 'flights.results.filters.apply.alliance', this.gaParams);
                    break;
                case 'filterFlightKind':
                    (<any>this).$ga.sendEvent('flights.results', 'filters.apply.type.flight', 'flights.results.filters.apply.type.flight', this.gaParams);
                    break;
            }
        } else {
            if(!this.filters.touched) {
                (<any>this).$ga.sendEvent('flights.results', 'filters.reset', 'flights.results.filters.reset', this.gaParams);
                this.gaAlreadySubmitted = this.gaAlreadySubmitted.filter((ga) => ga !== 'flights.results.filters.apply');
            }
        }
    }

    onFlightDurationChange(index: number) {
        // Update filter only on change event (when user stopped dragging)
        this.filters.flightDuration.items[index].rangeMin = this.flightDurationRanges[index][0];
        this.filters.flightDuration.items[index].rangeMax = this.flightDurationRanges[index][1];
        
        this.showFilterTooltip(document.querySelector('.flightDuration' + index));

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.time.flight', 'flights.results.filters.apply.time.flight', this.gaParams);
    }

    onFlightDurationClear() {
        this.filters.flightDuration.clear();

        this.resetFlightDuration();
    }

    onAirportsClear() {
        this.filters.airports.clear();
    }

    onDepartureArrivalChange(index, elementId) {
        this.filters.departureArrival.items[index].departure.rangeMin
            = this.departureArrivalRanges[index].departureRange[0];
        this.filters.departureArrival.items[index].departure.rangeMax
            = this.departureArrivalRanges[index].departureRange[1];
        this.filters.departureArrival.items[index].arrival.rangeMin
            = this.departureArrivalRanges[index].arrivalRange[0];
        this.filters.departureArrival.items[index].arrival.rangeMax
            = this.departureArrivalRanges[index].arrivalRange[1];

        this.showFilterTooltip(document.querySelector(`.${elementId}`));

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.time.departure.arrival', 'flights.results.filters.apply.time.departure.arrival', this.gaParams);
    }

    onDepartureArrivalClear() {
        this.filters.departureArrival.clear();

        this.resetDepartureArrival();
    }

    onPriceChange() {
        // Update filter only on change event (when user stopped dragging)
        this.filters.price.rangeMin = this.priceRange[0];
        this.filters.price.rangeMax = this.priceRange[1];

        this.showFilterTooltip((<any>this.$refs).price.$el);

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.price', 'flights.results.filters.apply.price', this.gaParams);
    }

    onPriceClear() {
        this.filters.price.clear();

        this.resetPrice();
    }

    onStopDurationChange() {
        // Update filter only on change event (when user stopped dragging)
        this.filters.stopDuration.rangeMin = this.stopDurationRange[0];
        this.filters.stopDuration.rangeMax = this.stopDurationRange[1];

        this.showFilterTooltip((<any>this.$refs).stopDuration.$el);

        (<any>this).$ga.sendEvent('flights.results', 'filters.apply.duration.transfer', 'flights.results.filters.apply.duration.transfer', this.gaParams);
    }

    onStopDurationClear() {
        this.filters.stopDuration.clear();

        this.resetStopDuration();
    }

    resetFlightDuration() {
        this.filters.flightDuration.items.forEach((item, index) => {
            this.flightDurationRanges[index] = [item.min, item.max];
        });
    }

    resetDepartureArrival() {
        this.filters.departureArrival.items.forEach((item, index) => {
            this.departureArrivalRanges[index] = {
                departureRange: [item.departure.min, item.departure.max],
                arrivalRange: [item.arrival.min, item.arrival.max]
            };
        });
    }

    resetPrice() {
        this.priceRange = [this.filters.price.rangeMin, this.filters.price.rangeMax];
    }

    resetStopDuration() {
        this.stopDurationRange = [this.filters.stopDuration.min, this.filters.stopDuration.max];
    }

    resetFilters() {
        (<any>this).$ga.sendEvent('flights.results', 'filters.reset', 'flights.results.filters.reset', this.gaParams);
        this.gaAlreadySubmitted = this.gaAlreadySubmitted.filter((ga) => ga !== 'flights.results.filters.apply');
        this.filters.clear();
        window.scrollTo(0, 0);

        this.resetFlightDuration();
        this.resetDepartureArrival();
        this.resetPrice();
        this.resetStopDuration();
    }

    showFilterTooltip(target: any) {
        if(!this.gaAlreadySubmitted.includes('flights.results.filters.apply')) {
            (<any>this).$ga.sendEvent('flights.results', 'filters.apply', 'flights.results.filters.apply', this.gaParams);
            this.gaAlreadySubmitted.push('flights.results.filters.apply');
        }
        (<any>this).$refs.filterTooltip.isActive = false;

        if (this.filterTooltipTimeout) {
            clearTimeout(this.filterTooltipTimeout);
        }

        this.$nextTick(() => {
            this.filterTooltipActivator = target;
            (<any>this).$refs.filterTooltip.isActive = true;

            this.filterTooltipTimeout = window.setTimeout(() => {
                (<any>this).$refs.filterTooltip.isActive = false;
            }, 2000);
        });
    }

    updateFlightData() {
        const lastHistoryItem = this.flightSearch.getHistoryItems()[0];

        if (lastHistoryItem) {
            this.flightData = {
                class: lastHistoryItem.class,
                departure: moment(lastHistoryItem.departure!).format(i18next.t('format_date_lg_wd')),
                from: lastHistoryItem.from,
                passengers: i18next.t('label_passengers_and_class_value', <any>{
                    i: lastHistoryItem.passengers,
                    j: i18next.t('label_flight_class_' + lastHistoryItem.class.toLowerCase())
                }),
                return: lastHistoryItem.return ? moment(lastHistoryItem.return!).format(i18next.t('format_date_lg_wd')) : null,
                to: lastHistoryItem.to
            };
        }
    }

    openSubscriptionModal() {
        (<any>this).$ga.sendEvent('price subscription', 'btn.click', this.getFlightDataGaLabel(), this.gaParams);
        this.updateFlightData();
        this.subscriptionModal.view = 'form';
        (<any>this.$refs).modalSubscription.isActive = true;
    }

    getFlightDataGaLabel() {
        const p = location.pathname.split('/');

        if (p.length === 7) {
            return `${p[2]}-${p[3]}.${p[4]}-0000.${p[5].split('').join('-')}`;
        } else {
            return `${p[2]}-${p[3]}.${p[4]}-${p[7]}.${p[8].split('').join('-')}`;
        }
    }

    subscribe() {
        if (this.searchId && this.subscriptionModal.email) {
            this.subscriptionModal.view = 'loading';
            new api.BestPriceService().subscribe({
                searchId: this.searchId,
                email: this.subscriptionModal.email,
                onlyFreeBaggage: this.subscriptionModal.onlyFreeBaggage,
                onlyDirectFlight: this.subscriptionModal.onlyDirectFlight
            }).then(
                () => {
                    this.subscriptionModal.view = 'success';
                },
                error => {
                    this.subscriptionModal.view = 'error';
                    console.error(error);
                }
            );
        }
    }
}
